import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Building2, Crown } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CaregiverAgencyOnboarding from '@/components/CaregiverAgencyOnboarding';
export default function AgencyCaregiverApplicationPage() {
  const [selectedPlan, setSelectedPlan] = useState<'agency' | 'premium' | null>(null);
  const navigate = useNavigate();
  if (selectedPlan === 'agency') {
    return <>
        <SEO
          title="Agency Application"
          description="Complete your agency application for SeniorThrive. Join our network as a care agency."
          canonical="https://seniorthrive.com/caregivers/apply/agency"
        />
        
        <div className="min-h-screen bg-gradient-to-br from-background to-muted/50 py-8">
          <div className="container mx-auto px-4">
            <Button variant="ghost" onClick={() => setSelectedPlan(null)} className="mb-6 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to agency options
            </Button>
            <CaregiverAgencyOnboarding planPreset="agency" />
          </div>
        </div>
      </>;
  }
  return <>
      <SEO
        title="Join as a Care Agency"
        description="Partner with SeniorThrive as a care agency. Choose from Agency Free or Premium partnership options."
        keywords="care agency, home care agency, senior care partnership, agency network"
        canonical="https://seniorthrive.com/caregivers/apply/agency"
      />

      <div className="min-h-screen bg-gradient-to-br from-background to-muted/50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-8">
            <Button variant="ghost" onClick={() => navigate('/caregivers/apply')} className="mb-6 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to caregiver options
            </Button>
            
            <h1 className="text-3xl font-bold mb-4">Choose Your Agency Partnership</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Select the partnership level that aligns with your agency's growth goals
            </p>
          </div>

          {/* Partnership Options */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Agency Free */}
            <Card className="cursor-pointer transition-all hover:shadow-lg border-2 border-transparent hover:border-primary/20">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-xl flex items-center justify-center gap-2">
                  Agency Free
                  <Badge variant="secondary">Free</Badge>
                </CardTitle>
                <p className="text-muted-foreground">Get started with basic agency partnership benefits and support</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    Agency profile in directory
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    Basic family inquiries
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    Agency management tools
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    Community networking
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    Staff caregiver profiles
                  </li>
                </ul>
                
                <Button onClick={() => setSelectedPlan('agency')} className="w-full" variant="outline">
                  Get Started - Free
                </Button>
              </CardContent>
            </Card>

            {/* Agency Premium */}
            <Card className="cursor-pointer transition-all hover:shadow-lg border-2 border-primary/20 relative overflow-hidden opacity-60">
              <div className="absolute inset-0 bg-gradient-to-br from-black/5 to-black/10 z-10" />
              <div className="absolute top-4 right-4 bg-orange-500 text-white px-3 py-1 text-xs font-medium rounded-full z-20">
                Coming Soon
              </div>
              <CardHeader className="text-center relative z-10">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-xl flex items-center justify-center gap-2">
                  Agency Premium
                  <Badge className="bg-gradient-to-r from-primary to-primary/80">Premium</Badge>
                </CardTitle>
                <p className="text-muted-foreground">
                  Enhanced partnership with priority features and support
                </p>
              </CardHeader>
              <CardContent className="space-y-4 relative z-10">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    All Agency Free features
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    Priority in family searches
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    Enhanced marketing tools
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    Dedicated partnership support
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    Advanced analytics dashboard
                  </li>
                </ul>
                
                <Button disabled className="w-full bg-gradient-to-r from-primary to-primary/80 opacity-60 cursor-not-allowed">
                  Coming Soon
                </Button>
                
                <p className="text-xs text-muted-foreground text-center">
                  Premium agency features launching Q2 2025
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Interest in Premium */}
          <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="pt-6 text-center">
              <h3 className="font-semibold mb-2">Interested in Agency Premium?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Be among the first to know when our premium agency partnership launches. 
                Start with Agency Free and we'll notify you about upgrade opportunities.
              </p>
              <Button onClick={() => setSelectedPlan('agency')} className="bg-primary hover:bg-primary/90">
                Start with Agency Free
              </Button>
            </CardContent>
          </Card>

          {/* Benefits Section */}
          <Card className="bg-muted/50 mt-8">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-center mb-4">Why Partner with SeniorThrive?</h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="font-medium mb-1">Quality Families</div>
                  <div className="text-muted-foreground">Connect with families seeking trusted care services</div>
                </div>
                <div className="text-center">
                  <div className="font-medium mb-1">Professional Network</div>
                  <div className="text-muted-foreground">Join a community of quality care providers</div>
                </div>
                <div className="text-center">
                  <div className="font-medium mb-1">Growth Support</div>
                  <div className="text-muted-foreground">Tools and resources to help your agency thrive</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>;
}